import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PaymentProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Statuses } from "assets/SideBarIcons/status.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import Loader from "layout/Loader";
import { GetPaymentByIdResponse } from "subModule/src/services/payment/interface";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData { [key: string]: any; }
export default function PaymentModeModal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "customers",
        moduleLabelKey: "Payment Modes",
        moduleHref: "/payment-modes",
        entityFallbackLabelKey: "title",
        isEdit: !!params?.id,
    });
    const { enqueueSnackbar } = useSnackbar();
    const { register, handleSubmit, formState: { errors, dirtyFields, isDirty }, reset } = useForm<FormData>({ defaultValues: {} });
    const loggedInUserData = useSelector(selectUserInfo);
    const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
    const isArabicLanguage = (lang?: string) => {
        const l = (lang || "").toLowerCase();
        return l === "arabic" || l === "ar" || l.includes("arab");
    };
    const localizedPaymentModePlaceholder = (lang?: string) =>
        isArabicLanguage(lang) ? t("phPaymentModeAr") : t("phPaymentModeEn");
    const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(params?.id ? false : true);
    useEffect(() => {
        if (params?.id) {
            if (loading) getByID();
        }
    }, [params]);
    useEffect(() => {
        if (isDirty && params?.id) setIsBtnEnabled(true);
    }, [isDirty, dirtyFields]);
    const getByID = () => {
        PaymentProfileService.getPaymentModeByID(+(params as any)?.id).then((res: GetPaymentByIdResponse) => {
            if (res.success) {
                reset({ ...(res?.data || {}), isDefault: res?.data?.default });
                captureLabel(res?.data?.title);
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        if (params?.id) {
            delete data.default;
            PaymentProfileService.updatePaymentMode(+params?.id, data).then((response: any) => {
                if (response.success) {
                    captureLabel(data?.title);
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('PaymentMode') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
                setIsBtnEnabled(true);
            }).catch((err: any) => {
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        } else {
            PaymentProfileService.createPaymentMode({ ...data }).then((response: any) => {
                if (response.success) {
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('PaymentMode') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate(-1);
                } else {
                    setIsBtnEnabled(true);
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
            }).catch((err: any) => {
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        }
    };

    return (<>
        <ComponentHeaderTwo
            breadcrumbItems={breadcrumbItems}
            showBtn={true}
            showSaveBtn={true}
            onClick={() => navigate(-1)}
            onSubmit={handleSubmit(onSubmit)}
            heading={params?.id ? t('editPaymentMode') : t('addPaymentMode')}
            icon={<Statuses height={28} width={28} />}
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={checkPrivileges("ROLE_PAYMENT_MODE_CREATE")}
        />
        {loading ? <Loader /> : <SimpleModal
            visable={true}
            title={params?.id ? "editPaymentMode" : "addPaymentMode"}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
                <Grid container component="form" id="form" spacing={3} onSubmit={handleSubmit(onSubmit)}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            {...register("title", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })}
                            label={`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`}
                            placeholder={localizedPaymentModePlaceholder(primaryLanguage)}
                            title={(`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`).replace(/\s+/g, ' ').trim()}
                            fullWidth
                            error={!!errors.title}
                            helperText={errors.title?.message as string}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            {...register("description")}
                            label={t('description')}
                            placeholder={t('phPaymentModeDescription')}
                            title={t('description')}
                            fullWidth
                            error={!!errors.description}
                            helperText={errors.description?.message as string}
                            InputLabelProps={{ shrink: true }}
                            multiline
                            rows={1}
                            sx={{
                                '& .MuiInputBase-fullWidth.MuiInputBase-multiline': { width: '100%', paddingRight: '0px', paddingBottom: 0 },
                                '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '29px' }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            type="number"
                            {...register("sortOrder")}
                            onChange={(e: any) => {
                                const value = parseInt(e.target.value);
                                e.target.value = value < 1 ? 1 : value > 100 ? 100 : value || "";
                                if (!IsBtnEnabled) setIsBtnEnabled(true);
                            }}
                            label={t("Sort Order (Value 1-100)")}
                            placeholder={t("phSortOrder")}
                            title={t("Sort Order (Value 1-100)")}
                            fullWidth
                            error={!!errors.sortOrder}
                            helperText={errors.sortOrder?.message as string}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            }
        />}
    </>)
}